<script lang="ts" setup>
import { ref, computed, onMounted, ComponentPublicInstance, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { BModal } from 'bootstrap-vue-next';

import './scss/Home.scss';
import './scss/ContratacionTerrestre.scss';

import Footer from './components/Footer.vue';
import WhatsappButton from './components/WhatsappButton.vue';

import {
  findClientByTributaryCode,
  findCompaniesGPS,
  createOrUpdateClient,
  createPolicyForTrygerLatam,
  getPricing,
} from '../../../repositories/landing.repository';
import GeoRefArgentinaRepository, {
  IProvince,
} from '../../../repositories/geoRefArgentina.repository';

import { wrongDepartureDateTime, today } from '../../../helpers';
import  {validationCUIT}  from '../../../helpers/validationCUIT';
import { IFinalCalculation } from '../../../helpers/finalCalculationAR';

import storePolicy from '@store/policy';
import moment from 'moment';
import { useI18n } from 'vue-i18n'
import CaptchaCloudflare from './components/CaptchaCloudflare.vue';

type CaptchaExpose = {
  reset: () => void
}
const { t } = useI18n();
const store = storePolicy();
const router = useRouter();
const refForm2 = ref<any>(null);
const refForm3 = ref<any>(null);
const inputsHidden = ref<boolean>(true);
const isOpenModal = ref<boolean>(false);
const checkedAuth = ref<boolean>(false);
const loading = ref<boolean>(false);
const policyId = ref<number | null>(1);
const error = ref<string>('');
const provincesARG = ref<IProvince[]>([]);
const companySelectedByParams = ref<{ nombre?: string; id: number } | null>(
  null,
);
const finalCalculation = ref<IFinalCalculation | null>(null);
const tokenCaptcha = ref<string | null>(null);
const captchaRef = ref<ComponentPublicInstance<CaptchaExpose> | null>(null);
const isSmallScreen = ref<boolean>(false);
const MOBILE_BREAKPOINT = 768;
const MOBILE_HALF_WIDTH_KEYS = new Set([
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'endForm',
  'finalCalculation'
]);
const MOBILE_STOPS_KEYS = new Set(['stops', 'stop1', 'stop2', 'stop3']);

const updateScreenSize = () => {
  if (typeof window === 'undefined') return;
  isSmallScreen.value = window.innerWidth < MOBILE_BREAKPOINT;
};

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
  const { subAsset, product, ...rest } = store.data;

  // Validamos si hay alguna propiedad (excepto subAsset y product) que sea null
  const thereAreNullProps = Object.values(rest).some((valor) => valor === null);

  // Validamos la relación entre subAsset y product
  const isProductRequiredButMissing = subAsset !== null && product === null;

  if (thereAreNullProps || isProductRequiredButMissing) {
    router.push('/');
  }
});

onMounted(async () => {
  const provinces = await GeoRefArgentinaRepository.getprovinces();
  provincesARG.value = provinces;
  const comapaniesGPS = await findCompaniesGPS();
  fieldsForm2.value = fieldsForm2.value.map((field: any) => {
    if (field.key === 'companyGPSId') {
      field.options = comapaniesGPS;
      const gpsCompany = store.data.queryParams?.gpsCompany;
      if (gpsCompany) {
        const company = comapaniesGPS.find((company) => {
          return company.slug === gpsCompany;
        });
        if (company) {
          companySelectedByParams.value = company;
          field.value = company;
          field.disabled = true;
          field.customClass =
            'multiselect--disabled .multiselect__select inputLanding';
        }
      }
    }
    if (field.key === 'province') {
      field.options = provinces;
    }

    return field;
  });
});

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return;
  window.removeEventListener('resize', updateScreenSize);
});

computed(() => fieldsForm3.value);

const fieldsForm1: any = ref([
  {
    key: 'cuit',
    title: t('landing.terrestre.contratacion_ar.cuit_del_cliente'),
    type: 'number',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.cuit_del_cliente_sin_guiones'),
    value: null,
    cols: 12,
    customClass: 'inputLanding text-uppercase',
    hidden: false,
    formatValue: (e: string) => e?.replace(/[^0-9]/g, '') ?? '',
    errors: [],
  },
]);

const fieldsForm2: any = ref([
  {
    key: 'tradeName',
    title: t('landing.terrestre.contratacion_ar.cuit_del_cliente_sin_guiones'),
    type: 'text',
    validations: {
      rules: {
        required: true,
		max: 20,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.cuit_del_cliente_sin_guiones'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
    formatValue: (e: string) => e?.toUpperCase() ?? '',
  },
  {
    key: 'email',
    title: t('landing.terrestre.contratacion_ar.email_del_cliente'),
    type: 'email',
    validations: {
      rules: {
        required: true,
        email: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
		 email: t('global.email_invalido'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.email_del_cliente'),
    value: store.data.email ?? null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'phone1',
    title: t('landing.terrestre.contratacion_ar.telefono_del_cliente'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.telefono_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'companyGPSId',
    title: t('landing.terrestre.contratacion_ar.empresa_gps'),
    selectLabel: null,
    type: 'select',
    disabled: false,
    placeholder: t('landing.terrestre.contratacion_ar.empresa_gps'),
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: [],
  },
  {
    key: 'address',
    title: t('landing.terrestre.contratacion_ar.direccion_del_cliente'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.direccion_del_cliente'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'postCode',
    title: t('landing.terrestre.contratacion_ar.codigo_postal'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.codigo_postal_placeholder'),
    value: null,
    cols: 12,
    customClass: 'inputLanding',
  },
  {
    key: 'province',
    title: t('landing.terrestre.contratacion_ar.provincia'),
    selectLabel: null,
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.provincia'),
    value: null,
    cols: 12,
    customClass: 'inputSelect',
    options: [],
    listenSelect: true,
  },
  {
    key: 'city',
    title: t('landing.terrestre.contratacion_ar.localidad'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.localidad'),
    value: null,
    options: [],
    cols: 12,
    customClass: 'inputSelect',
  },
]);

const fieldsForm3: any = ref([
  { slot: true, key: 'title' },
  {
    key: 'typeOfMerchandise',
    title: t('landing.terrestre.contratacion_ar.tipo_de_mercancia'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.tipo_de_mercancia'),
    value: store.data?.asset?.nombre,
    cols: 12,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit',
    title: t('landing.terrestre.contratacion_ar.limite_de_indemnizacion'),
    type: 'number',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.limite_de_indemnizacion'),
    value: store.data.indemnificationLimit,
    cols: 6,
    customClass: 'inputLanding',
    disabled: true,
  },
  {
    key: 'indemnificationLimit-calc',
    title: t('landing.terrestre.contratacion_ar.precio'),
    type: 'text',
    value: `$${store.data.endPrice} + IVA`,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.precio_mas_iva'),
    disabled: true,
    cols: 6,
    customClass: 'inputLanding',
  },
  {
    key: 'originProvince',
    title: t('landing.terrestre.contratacion_ar.provincia_de_origen'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    options: provincesARG,
    placeholder: t('landing.terrestre.contratacion_ar.provincia_de_origen'),
    value: null,
    cols: 6,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'originCity',
    title: t('landing.terrestre.contratacion_ar.localidad_de_origen'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.localidad_de_origen'),
    value: null,
    cols: 6,
    options: [],
    customClass: 'inputSelect',
    disabled: true,
  },
  {
    key: 'destinationProvince',
    title: t('landing.terrestre.contratacion_ar.provincia_de_destino_final'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.provincia_de_destino_final'),
    value: null,
    options: provincesARG,
    cols: 6,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'destinationCity',
    title: t('landing.terrestre.contratacion_ar.localidad_de_destino_final'),
    type: 'select',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    placeholder: t('landing.terrestre.contratacion_ar.localidad_de_destino_final'),
    value: null,
    cols: 6,
    options: [],
    customClass: 'inputSelect',
    disabled: true,
  },
  {
    key: 'stops',
    title: t('landing.terrestre.contratacion_ar.paradas_intermedias'),
    selectLabel: null,
    type: 'select',
    placeholder: t('landing.terrestre.contratacion_ar.paradas_intermedias_placeholder'),
    value: null,
    cols: 3,
    customClass: 'inputSelect',
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    options: [
      { nombre: 'Si', id: 1 },
      { nombre: 'No', id: 0 },
    ],
    listenSelect: true,
  },
  {
    key: 'stop1',
    title: t('landing.terrestre.contratacion_ar.parada_1'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.parada_1'),
    value: null,
    cols: 3,
    disabled: true,
    validations: {
      rules: {
        required: false,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'stop2',
    title: t('landing.terrestre.contratacion_ar.parada_2'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.parada_2'),
    value: null,
    cols: 3,
    disabled: true,
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'stop3',
    title: t('landing.terrestre.contratacion_ar.parada_3'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.parada_3'),
    value: null,
    cols: 3,
    disabled: true,
    customClass: 'inputLanding inputLanding--mt',
  },
  {
    key: 'startDate',
    title: t('landing.terrestre.contratacion_ar.fecha_inicio_viaje'),
    type: 'date',
    placeholder: t('landing.terrestre.contratacion_ar.fecha_inicio_viaje'),
    value: null,
    cols: 6,
    min: today(),
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'startTime',
    title: t('landing.terrestre.contratacion_ar.hora_inicio_viaje'),
    type: 'time',
    placeholder: t('landing.terrestre.contratacion_ar.hora_inicio_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
    listenFocus: true,
  },
  {
    key: 'endDate',
    title: t('landing.terrestre.contratacion_ar.fecha_fin_viaje'),
    type: 'date',
    placeholder: t('landing.terrestre.contratacion_ar.fecha_fin_viaje'),
    value: null,
    cols: 6,
    min: today(),
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'endTime',
    title: t('landing.terrestre.contratacion_ar.hora_fin_viaje'),
    type: 'time',
    placeholder: t('landing.terrestre.contratacion_ar.hora_fin_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'documentDescription',
    title: t('landing.terrestre.contratacion_ar.documentos_del_viaje'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.documentos_del_viaje'),
    value: null,
    cols: 6,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'document',
    title: t('landing.terrestre.contratacion_ar.adjuntar_documentos'),
    type: 'file',
    placeholder: t('landing.terrestre.contratacion_ar.adjuntar_documentos'),
    value: null,
    cols: 6,
    hidden: false,
    customClass: 'inputFile',
  },
  {
    key: 'truckPlate',
    title: t('landing.terrestre.contratacion_ar.placa_tractor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.placa_tractor'),
    value: null,
    cols: 4,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'box-trailer',
    title: t('landing.terrestre.contratacion_ar.caja_de_carga'),
    selectLabel: null,
    type: 'select',
    placeholder: t('landing.terrestre.contratacion_ar.caja_de_carga'),
    value: null,
    cols: 4,
    options: [
      { nombre: 'Si', id: 1 },
      { nombre: 'No', id: 0 },
    ],
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    hidden: false,
    customClass: 'inputSelect',
    listenSelect: true,
  },
  {
    key: 'trailerPlate',
    title: t('landing.terrestre.contratacion_ar.placa_caja'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.placa_caja'),
    value: null,
    cols: 4,
    customClass: 'inputLanding',
    validations: {
      rules: {
        required: false,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    disabled: true,
  },
  {
    key: 'driverName',
    title: t('landing.terrestre.contratacion_ar.nombre_del_conductor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.nombre_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'driverCellphone',
    title: t('landing.terrestre.contratacion_ar.movil_del_conductor'),
    type: 'text',
    placeholder: t('landing.terrestre.contratacion_ar.movil_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        required: true,
        max: 12,
        min: 8,
      },
	  messages: {
		 required: t('global.es_obligatorio'),
	  },
    },
    customClass: 'inputLanding',
  },
  {
    key: 'driverEmail',
    title: t('landing.terrestre.contratacion_ar.email_del_conductor'),
    type: 'email',
    placeholder: t('landing.terrestre.contratacion_ar.email_del_conductor'),
    value: null,
    cols: 12,
    validations: {
      rules: {
        email: true,
      },
	  messages: {
		 email: t('global.email_invalido'),
	  },
    },
    hidden: false,
    customClass: 'inputLanding',
  },
  { slot: true, key: 'finalCalculation', cols: 12 },
  { slot: true, key: 'endForm', cols: 12 },
]);

const responsiveFieldsForm1 = computed(() =>
  fieldsForm1.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

const responsiveFieldsForm2 = computed(() =>
  fieldsForm2.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

const responsiveFieldsForm3 = computed(() =>
  fieldsForm3.value.map((field: any) => ({
    ...field,
    cols:
      isSmallScreen.value && MOBILE_STOPS_KEYS.has(field.key)
        ? 6
        : isSmallScreen.value && !MOBILE_HALF_WIDTH_KEYS.has(field.key)
        ? 12
        : field.cols,
  })),
);

// Handles forms changes
const updateFieldsInArray = (
  array: any,
  updates: any,
  preservedValues: any = null,
) => {
  for (const update of updates) {
    const field = array.find((field: any) => field.key === update.key);
    if (field) {
      if ('type' in update) {
        field.type = update.type;
      }
      if ('options' in update) {
        field.options = update.options;
      }
      if ('value' in update) {
        field.value = update.value;
      }
      if ('disabled' in update) {
        field.disabled = update.disabled;
      }

      if ('customClass' in update) {
        field.customClass = update.customClass;
      }
    }
  }

  if (preservedValues) {
    for (const key in preservedValues) {
      const field = array.find((field: any) => field.key === key);
      if (field) {
        field.value = preservedValues[key];
      }
    }
  }
};

// validation CUIT
const handleOnSubmitCUIT = async (e: any) => {
  const checkCuil = validationCUIT(e.items.cuit);

  if (!e.isFormValid || !checkCuil.valid || checkCuil.type === 'Tipo desconocido') {
    return (fieldsForm1.value[0].errors = ['CUIT es invalido']);
  }
  loading.value = true;
  const { client } = await findClientByTributaryCode(e.items.cuit);

  if (client) {
    store.setProp({ key: 'client', value: client });
  }

  if (client) {
    const updatesClientInputs = [
      { key: 'tradeName', value: client.tradeName },
      { key: 'phone1', value: client.phone1 },
      {
        key: 'companyGPSId',
        value: {
          id: companySelectedByParams.value?.id ?? client.companyGPS.id,
          nombre:
            companySelectedByParams.value?.nombre ??
            client.companyGPS.description,
        },
      },
      { key: 'address', value: client.address },
      { key: 'province', value: { nombre: client.regionProvince } },
      { key: 'city', value: { nombre: client.location } },
      { key: 'postCode', value: client.postCode },
    ];

    updateFieldsInArray(fieldsForm2.value, updatesClientInputs);
  }
  fieldsForm1.value.find((field: any) => field.key === 'cuit').disabled = true;
  fieldsForm2.value.find((field: any) => field.key === 'tradeName').validations.rules.max = checkCuil.type === 'Persona Jurídica' ? 40 : 20;
  loading.value = false;
  inputsHidden.value = false;

  if (!client) {
    store.setProp({ key: 'client', value: { cuit: e.items.cuit } });
  } else {
    finalCalculation.value = await getPricing(
      store.data.indemnificationLimit,
      e.items.cuit,
      client.regionProvince!,
    );
  }
};

// Handles form changes for refForm3
const handleSelectChange = async (e: any) => {
  const updateFields: {
    key: string;
    disabled?: boolean;
    'validations.rules.required'?: boolean;
    options?: any[];
  }[] = [];

  const updateCommonFields = (key: string, isEnabled: boolean) => {
    updateFields.push(
      { key, disabled: !isEnabled },
      { key, 'validations.rules.required': isEnabled },
    );
  };

  const handleCityUpdate = async (cityKey: string) => {
    const cities = await GeoRefArgentinaRepository.getCities(e.value.id);
    await refForm3.value.setValue({ key: cityKey, value: null });

    const updateCityFields = [
      {
        key: cityKey,
        type: e.value.id !== 99 ? 'select' : 'text',
        options: e.value.id !== 99 ? cities : undefined,
        customClass: e.value.id !== 99 ? 'inputSelect' : 'inputLanding',
        disabled: false,
      },
    ];
    updateFieldsInArray(fieldsForm3.value, updateCityFields);
  };

  switch (e.input.key) {
    case 'box-trailer':
      updateCommonFields('trailerPlate', !!e.value.id);
      break;

    case 'originProvince':
      await handleCityUpdate('originCity');
      return;

    case 'destinationProvince':
      await handleCityUpdate('destinationCity');
      return;

    default:
      ['stop1', 'stop2', 'stop3'].forEach((stop) =>
        updateCommonFields(stop, !!e.value.id),
      );
      break;
  }

  updateFieldsInArray(fieldsForm3.value, updateFields);
};

// The function retrieves localities when a province is selected
// Once the client's province is obtained, we can generate the final policy calculation
const handleGetCities = async () => {
  const { items } = await refForm2.value.getValues();
  const preservedValues = {
    tradeName: items.tradeName,
    email: items.email,
    phone1: items.phone1,
    companyGPSId: items.companyGPSId,
    address: items.address,
    province: items.province,
  };

  if (items.province.id !== 99) {
    const cities = await GeoRefArgentinaRepository.getCities(items.province.id);
    await refForm2.value.setValue({ key: 'city', value: null });
    updateFieldsInArray(
      fieldsForm2.value,
      [
        {
          key: 'city',
          type: 'select',
          options: cities,
          value: null,
          customClass: 'inputSelect',
        },
      ],
      preservedValues,
    );
  } else {
    await refForm2.value.setValue({ key: 'city', value: null });
    updateFieldsInArray(
      fieldsForm2.value,
      [
        {
          key: 'city',
          type: 'text',
          value: null,
          customClass: 'inputLanding',
        },
      ],
      preservedValues,
    );
  }

  finalCalculation.value = await getPricing(
    store.data.indemnificationLimit,
    store.client.cuit,
    items.province.nombre,
  );
};

// Submit form with all values
const handleOnSubmitPolicies = async ({ items, isFormValid }: any) => {
  const { items: itemsForm2, isFormValid: isFormValidForm2 } =
    await refForm2.value.getValues();

  if (!tokenCaptcha.value) {
    return (error.value = t('landing.terrestre.contratacion_ar.por_favor_verifica_el_recaptcha'));
  }

  if (!checkedAuth.value) {
    return (error.value = t('landing.terrestre.contratacion_ar.terminos_y_condiciones_no_aceptados'));
  }

  if (!isFormValidForm2) {
    return (error.value = t('landing.terrestre.contratacion_ar.el_formulario_debe_estar_completo'));
  }
  if (!isFormValid) {
    return (error.value = t('landing.terrestre.contratacion_ar.el_formulario_debe_estar_completo'));
  }

  const {
    address,
    companyGPSId,
    email,
    phone1,
    tradeName,
    city,
    province,
    postCode,
  } = itemsForm2;

  const {
    typeOfMerchandise,
    indemnificationLimit,
    originProvince,
    destinationProvince,
    originCity,
    destinationCity,
    stops,
    stop1,
    stop2,
    stop3,
    truckPlate,
    trailerPlate,
    driverName,
    documentDescription,
    driverCellphone,
    driverEmail,
    startDate,
    endDate,
    startTime,
    endTime,
    document,
  } = items;

  const isOkDepartureDateTime = wrongDepartureDateTime(startDate, startTime, 'cl');

  if (isOkDepartureDateTime) {
    return (error.value =
	t('landing.terrestre.contratacion_ar.la_hora_de_salida_no_puede_ser_menor'));
  }
  if (tradeName.split(' ').length < 2) {
    return (error.value = t('landing.terrestre.contratacion_ar.debe_proporcionar_al_menos_un_nombre'));
  }

  try {
    loading.value = true;

    // first Request create or updateClient
    const createOrUpdateClientData = {
      tradeName,
      address,
      email,
      phone1,
      location: city.nombre ?? city,
      postCode,
      cuit: store.client.cuit,
      regionProvince: province.nombre,
      companyGPSId: store.client?.companyGPS?.id ?? companyGPSId.id,
      urlDomain: window.location.origin,
    };

    const client = await createOrUpdateClient(
      store.client.id,
      createOrUpdateClientData,
	  'AR'
    );
    store.setProp({
      key: 'client',
      value: { ...store.client, id: client.data.client.id },
    });

    // second Request create policy
    const travelStartDate = new Date(`${startDate}T${startTime}`);
  	const travelEndDate = new Date(`${endDate}T${endTime}`);
	const fechaISOstart = moment(travelStartDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
	const fechaISOend = moment(travelEndDate).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    const assetId = store.data.asset.id;
	const subAssetId = store.data.subAsset?.id ?? null;
    const productId = store.data.product?.id ?? null;
    const parseProvince =
      province.nombre === 'Ciudad Autónoma de Buenos Aires'
        ? 'Buenos aires'
        : province.nombre;

    const createPolicyData: any = {
      tradeName,
      assetId,
	  subAssetId,
	  productId,
      phone1,
      typeOfMerchandise,
      indemnificationLimit,
      originRegionProvince: originProvince.nombre,
      destinationRegionProvince: destinationProvince.nombre,
      originLocation: originCity.nombre ?? originCity,
      destinationLocation: destinationCity.nombre ?? destinationCity,
      stop1,
      stop2,
      stop3,
      trailerPlate,
      truckPlate,
      driverName,
      documentDescription,
      driverCellphone,
      driverEmail,
      travelStartDate: fechaISOstart,
      travelEndDate: fechaISOend,
      hasStops: stops.nombre,
      companyGPSId: companyGPSId.id,
      document: document?.length ? document[0].image : null,
      clientName: tradeName,
      tributaryCode: store.client.cuit,
      address,
      postalCode: postCode,
      location: city.nombre,
      regionProvince: parseProvince,
      email,
      endPrice: finalCalculation?.value?.primaTotal,
	  token: tokenCaptcha.value
    };

    const urlForPayment = await createPolicyForTrygerLatam(
      client.data.client.id,
      client.data.manager.id,
      createPolicyData,
    );

    window.location.replace(urlForPayment);
    loading.value = false;
  } catch (e) {
    error.value = 'Ocurrió un Error.';
    loading.value = false;
  }finally{
	 captchaRef.value?.reset()
  }
};

const hideModal = () => {
  isOpenModal.value = false;
};
const handleTokenCaptcha = (token: string) => {
  tokenCaptcha.value = token;
};

const handleOnExpired = () => {
  tokenCaptcha.value = null;
};
</script>

<template>
  <h1 class="landing__title">{{ t('landing.terrestre.contratacion_ar.seguro_de_mercancias_por_trayecto') }}</h1>
  <h2 class="landing__subtitle">{{ t('landing.terrestre.contratacion_ar.ya_falta_menos') }}</h2>
  <h2 class="landing__subtitle">{{ t('landing.terrestre.contratacion_ar.ayudanos_completando') }}</h2>
  <div class="contratacion">
    <div class="contratacion__container">
      <h2 class="text-center text-white">{{ t('landing.terrestre.contratacion_ar.datos_del_cliente') }}</h2>
      <Form
        ref="refTableForm"
        :inputs="responsiveFieldsForm1"
        @onSubmit="handleOnSubmitCUIT"
      >
        <template #buttons>
          <div class="d-flex" v-if="inputsHidden">
            <CButton
              class="p-4 w-100 my-2 px-2"
              background="background-green"
              style="font-size: 1rem"
            >
              <template #content>
                <div>
                  <BSpinner v-if="loading" />
                  <span v-else>{{ t('landing.terrestre.contratacion_ar.comprobar_cuit') }}</span>
                </div>
              </template>
            </CButton>
          </div>
        </template>
      </Form>
      <template v-if="!inputsHidden">
        <Form
          ref="refForm2"
          :inputs="responsiveFieldsForm2"
          @select="handleGetCities"
        ></Form>
        <Form
          ref="refForm3"
          :inputs="responsiveFieldsForm3"
          @onSubmit="handleOnSubmitPolicies"
          @select="handleSelectChange"
        >
          <template #title>
            <h2 class="text-center text-white mt-5">{{ t('landing.terrestre.contratacion_ar.datos_del_viaje') }}</h2>
          </template>
          <template #endForm>
            <h2 class="text-white fs-5 text-decoration-underline">
				{{ t('landing.terrestre.contratacion_ar.terminos_y_condiciones') }}
            </h2>
            <b-form-checkbox v-model="checkedAuth" name="check-button" switch>
              <p class="text-white">
				  {{ t('landing.terrestre.contratacion_ar.declaro_haber_leido') }}
                <span
                  @click="isOpenModal = true"
                  class="text-decoration-underline"
                  >{{ t('landing.terrestre.contratacion_ar.condiciones_generales_particulares') }}</span
                >
                {{ t('landing.terrestre.contratacion_ar.de_esta_poliza') }}
              </p>
            </b-form-checkbox>
          </template>
          <template #finalCalculation>
            <div v-if="finalCalculation" class="container p-0 mt-2">
              <b-card class="rounded-4">
                <h2 class="text-center text-primary">
					{{ t('landing.terrestre.contratacion_ar.calculo_de_prima_fina_y_pago') }}
                </h2>
                <b-row class="mt-3">
                  <b-col>
                    <b-list-group>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-light"
                      >
                        <span><strong>{{ t('landing.terrestre.contratacion_ar.monto_imponible') }}</strong></span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.montoImponible }}</span
                        >
                      </b-list-group-item>
                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('landing.terrestre.contratacion_ar.taxes') }}</span>
                      </b-list-group-item>

                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center"
                      >
                        <span>{{ t('landing.terrestre.contratacion_ar.iva') }}</span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.IVA }}</span
                        >
                      </b-list-group-item>

                      <b-list-group-item
                        class="d-flex justify-content-between align-items-center bg-primary text-white"
                      >
                        <span><strong>{{ t('landing.terrestre.contratacion_ar.prima_total') }}</strong></span>
                        <span class="font-weight-bold"
                          >${{ finalCalculation.primaTotal }}</span
                        >
                      </b-list-group-item>
                    </b-list-group>
                  </b-col>
                </b-row>
              </b-card>
            </div>
          </template>

          <template #buttons>
            <p v-if="error" class="text-danger-emphasis mt-3">{{ error }}</p>
			 <CaptchaCloudflare
				ref="captchaRef"
				@verified="handleTokenCaptcha"
				@expired="handleOnExpired"
			/>
            <div class="d-flex">
              <CButton
                class="p-4 w-100 my-2 px-2"
                background="background-green"
                style="font-size: 1.5rem"
              >
                <template #content>
                  <div>
                    <BSpinner v-if="loading" />
                    <span v-else>{{ t('landing.terrestre.contratacion_ar.pagar_el_seguro') }}</span>
                  </div>
                </template>
              </CButton>
            </div>
            <div class="mt-3">
              <p class="text-white">
				{{ t('landing.terrestre.contratacion_ar.disclaimer_productos') }}
              </p>
              <p class="text-white">
                {{ t('landing.terrestre.contratacion_ar.disclaimer_ssn') }}
              </p>
            </div>
          </template>
        </Form>
      </template>
    </div>
  </div>
  <Footer />
  <WhatsappButton />
  <BModal
    :modelValue="isOpenModal"
    hide-footer
    static
    @hide="hideModal"
    size="lg"
    v-if="policyId"
  >
    <h4 class="text-center">{{ t('landing.terrestre.contratacion_ar.terminos_y_condiciones') }}</h4>
    <ul>
      <li class="mb-4">
        {{ t('landing.terrestre.contratacion_ar.punto_1') }}
      </li>
      <li class="mb-4">
        {{ t('landing.terrestre.contratacion_ar.punto_2') }}

        <a
          href="https://www.chubb.com/ar-es/politica-de-privacidad.html"
          target="_blank"
          >https://www.chubb.com/ar-es/politica-de-privacidad.html</a
        >
		{{ t('landing.terrestre.contratacion_ar.punto_2_1') }}
      </li>
      <li class="mb-4">
        {{ t('landing.terrestre.contratacion_ar.punto_3') }}
      </li>
      <li class="mb-4">
		{{ t('landing.terrestre.contratacion_ar.punto_4') }}
      </li>
      <li class="mb-4">
        {{ t('landing.terrestre.contratacion_ar.punto_5') }}
      </li>
      <li class="mb-4">
        {{ t('landing.terrestre.contratacion_ar.punto_6') }}
        <a
          href="https://www.Chubb.Com/ar-es/servicios/arrepentimiento_baja.Html"
          target="_blank"
          >https://www.Chubb.Com/ar-es/servicios/arrepentimiento_baja.Html</a
        > {{ t('landing.terrestre.contratacion_ar.punto_6_1') }}
      </li>
    </ul>
  </BModal>
</template>
